import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Award, Copy, ExternalLink, Eye, Package, Pencil, Plus, Printer, Trash2, Upload, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading, ErrorState } from "@/components/States";
import HandoverView from "@/components/handover/HandoverView";

const SECTIONS = [
  ["thankyou", "Thank-you note & team photo"], ["gallery", "Project photos"], ["finishes", "Finishes & paint colours"],
  ["warranties", "Warranties"], ["documents", "Documents & plans"], ["trades", "Trades & suppliers"], ["contract", "Contract summary"],
  ["firstweek", "Settling in (first week)"], ["review", "Review request"],
];
const BLANK = { item: "", provider: "", category: "", start_date: "", length: "", notes: "", file_ids: [] };

function WarrantyDialog({ projectId, open, onOpenChange, editing, onSaved }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (open) setF(editing ? { ...BLANK, ...editing, file_ids: editing.file_ids || [] } : BLANK); }, [open, editing]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const upload = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file); fd.append("project_id", projectId); fd.append("kind", "warranty");
        const { data } = await api.post("/files", fd);
        setF((p) => ({ ...p, file_ids: [...p.file_ids, data.file_id] }));
      }
    } catch { toast.error("Upload failed — files must be under 15 MB."); }
    setUploading(false);
  };

  const save = async () => {
    if (!f.item.trim()) return toast.error("Give the warranty an item name.");
    setBusy(true);
    try {
      const body = { item: f.item, provider: f.provider, category: f.category, start_date: f.start_date || null, length: f.length, notes: f.notes, file_ids: f.file_ids };
      if (editing) await api.put(`/warranties/${editing.id}`, body);
      else await api.post(`/projects/${projectId}/warranties`, body);
      toast.success(editing ? "Warranty updated" : "Warranty added");
      onOpenChange(false); onSaved();
    } catch { toast.error("Could not save the warranty."); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="warranty-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit warranty" : "Add a warranty"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Item *</Label><Input value={f.item} onChange={set("item")} placeholder="e.g. Kitchen appliances, Roof shingles" data-testid="warranty-item" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Provider / brand</Label><Input value={f.provider} onChange={set("provider")} placeholder="Bosch, GAF…" data-testid="warranty-provider" /></div>
            <div><Label>Category</Label><Input value={f.category} onChange={set("category")} placeholder="Appliance, Roofing…" data-testid="warranty-category" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" value={f.start_date || ""} onChange={set("start_date")} data-testid="warranty-start" /></div>
            <div><Label>Length</Label><Input value={f.length} onChange={set("length")} placeholder="2 years, Lifetime…" data-testid="warranty-length" /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={set("notes")} rows={2} placeholder="Serial numbers, coverage details, how to claim…" data-testid="warranty-notes" /></div>
          <div>
            <Label>Supporting documents</Label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {f.file_ids.map((id) => (
                <span key={id} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs" data-testid="warranty-file-chip">
                  Document <button onClick={() => setF((p) => ({ ...p, file_ids: p.file_ids.filter((x) => x !== id) }))} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
                </span>
              ))}
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs hover:bg-secondary" data-testid="warranty-upload">
                <Upload className="h-3 w-3" />{uploading ? "Uploading…" : "Add file"}
                <input type="file" multiple className="hidden" onChange={upload} accept="image/*,application/pdf" />
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="warranty-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HandoverTab({ projectId }) {
  const { data, loading, error, reload } = useFetch(`/projects/${projectId}/handover`);
  const warranties = useFetch(`/projects/${projectId}/warranties`);
  const photos = useFetch(`/projects/${projectId}/photos`);
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(false);
  const [wOpen, setWOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const teamRef = useRef(null);

  const uploadTeam = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("project_id", projectId); fd.append("kind", "photo");
      const { data } = await api.post("/files", fd);
      setCfg((p) => ({ ...p, team_photo_file_id: data.file_id }));
      photos.reload();
      toast.success("Team photo added");
    } catch { toast.error("Upload failed — images must be under 15 MB."); }
  };

  useEffect(() => { if (data?.config) setCfg(data.config); }, [data]);
  if (loading && !data) return <Loading rows={4} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!cfg) return <Loading rows={4} />;

  const published = cfg.published;
  const publicUrl = cfg.share_token ? `${window.location.origin}/handover/${cfg.share_token}` : null;
  const setSection = (k, v) => setCfg((p) => ({ ...p, sections: { ...p.sections, [k]: v } }));
  const payload = () => ({ title: cfg.title, intro: cfg.intro, hero_file_id: cfg.hero_file_id, team_photo_file_id: cfg.team_photo_file_id,
    first_week: cfg.first_week, review_url: cfg.review_url, review_text: cfg.review_text, sections: cfg.sections });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/handover`, payload());
      toast.success("Saved"); reload();
    } catch { toast.error("Could not save."); }
    setBusy(false);
  };
  const publish = async () => {
    setBusy(true);
    try { await api.put(`/projects/${projectId}/handover`, payload());
      const { data: r } = await api.post(`/projects/${projectId}/handover/publish`); setCfg((p) => ({ ...p, published: true, share_token: r.share_token })); toast.success("Published — link is live"); reload();
    } catch { toast.error("Could not publish."); }
    setBusy(false);
  };
  const unpublish = async () => { setBusy(true); try { await api.post(`/projects/${projectId}/handover/unpublish`); setCfg((p) => ({ ...p, published: false })); toast.success("Link turned off"); reload(); } catch { toast.error("Failed."); } setBusy(false); };
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };
  const delWarranty = async (id) => { try { await api.delete(`/warranties/${id}`); warranties.reload(); } catch { toast.error("Could not delete."); } };

  if (preview) {
    return (
      <div data-testid="handover-preview">
        <div className="mb-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setPreview(false)} data-testid="handover-close-preview"><X className="mr-1.5 h-4 w-4" />Close preview</Button>
          <span className="text-xs text-muted-foreground">Preview — this is what your client sees</span>
        </div>
        <div className="overflow-hidden rounded-2xl border"><HandoverView pack={{ ...data.pack, ...cfg }} showPrint={false} /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="handover-tab">
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2"><Package className="h-5 w-5 text-accent" /><div><h2 className="section-title">Handover Pack</h2><p className="text-sm text-muted-foreground">A branded package for your client at the end of the build — photos, finishes, warranties, trades, documents and their contract summary.</p></div></div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${published ? "bg-emerald-100 text-emerald-800" : "bg-secondary text-muted-foreground"}`} data-testid="handover-status">{published ? "Published" : "Draft"}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setPreview(true)} data-testid="handover-preview-btn"><Eye className="mr-1.5 h-4 w-4" />Preview</Button>
          {!published && <Button size="sm" className="rounded-full" onClick={publish} disabled={busy} data-testid="handover-publish-btn">Publish & create link</Button>}
          {published && publicUrl && <>
            <Button variant="outline" size="sm" className="rounded-full" onClick={copyLink} data-testid="handover-copy-link"><Copy className="mr-1.5 h-4 w-4" />Copy link</Button>
            <Button asChild variant="outline" size="sm" className="rounded-full"><a href={publicUrl} target="_blank" rel="noreferrer" data-testid="handover-open-page"><ExternalLink className="mr-1.5 h-4 w-4" />Open page</a></Button>
            <Button asChild variant="outline" size="sm" className="rounded-full"><a href={publicUrl} target="_blank" rel="noreferrer" data-testid="handover-print-page"><Printer className="mr-1.5 h-4 w-4" />Print / PDF</a></Button>
            <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={unpublish} disabled={busy} data-testid="handover-unpublish-btn">Turn off link</Button>
          </>}
        </div>
        {published && publicUrl && <p className="mt-2 break-all rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground" data-testid="handover-link-text">{publicUrl}</p>}
      </div>

      <div className="rounded-2xl border bg-card p-5">
        <h3 className="section-title mb-3">Cover & message</h3>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={cfg.title} onChange={(e) => setCfg((p) => ({ ...p, title: e.target.value }))} placeholder="Your Home Handover" data-testid="handover-title-input" /></div>
          <div><Label>Thank-you note</Label><Textarea value={cfg.intro} onChange={(e) => setCfg((p) => ({ ...p, intro: e.target.value }))} rows={4} placeholder="A short personal note to your client about their finished project…" data-testid="handover-intro-input" /></div>
          <div>
            <Label>Cover photo</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="handover-hero-picker">
              <button onClick={() => setCfg((p) => ({ ...p, hero_file_id: null }))} className={`grid h-16 w-16 place-items-center rounded-lg border text-xs ${!cfg.hero_file_id ? "border-primary bg-primary/5" : "text-muted-foreground"}`}>None</button>
              {(photos.data || []).slice(0, 12).map((ph) => (
                <button key={ph.file_id} onClick={() => setCfg((p) => ({ ...p, hero_file_id: ph.file_id }))} className={`h-16 w-16 overflow-hidden rounded-lg border-2 ${cfg.hero_file_id === ph.file_id ? "border-primary" : "border-transparent"}`} data-testid="handover-hero-option">
                  <img src={fileUrl(ph.url)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            {!photos.data?.length && <p className="mt-1 text-xs text-muted-foreground">Add project photos to choose a cover image.</p>}
          </div>
          <div>
            <Label>Team photo</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="handover-team-picker">
              <button onClick={() => setCfg((p) => ({ ...p, team_photo_file_id: null }))} className={`grid h-16 w-16 place-items-center rounded-lg border text-xs ${!cfg.team_photo_file_id ? "border-primary bg-primary/5" : "text-muted-foreground"}`}>None</button>
              {(photos.data || []).slice(0, 12).map((ph) => (
                <button key={ph.file_id} onClick={() => setCfg((p) => ({ ...p, team_photo_file_id: ph.file_id }))} className={`h-16 w-16 overflow-hidden rounded-lg border-2 ${cfg.team_photo_file_id === ph.file_id ? "border-primary" : "border-transparent"}`} data-testid="handover-team-option">
                  <img src={fileUrl(ph.url)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
              <button onClick={() => teamRef.current?.click()} className="grid h-16 w-16 place-items-center rounded-lg border border-dashed text-xs text-muted-foreground hover:bg-secondary" data-testid="handover-team-upload"><Upload className="h-4 w-4" /></button>
              <input ref={teamRef} type="file" accept="image/*" className="hidden" onChange={uploadTeam} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Shown with your thank-you note. Pick a project photo or upload a team shot.</p>
          </div>
          <div><Label>Settling in — your first week</Label><Textarea value={cfg.first_week || ""} onChange={(e) => setCfg((p) => ({ ...p, first_week: e.target.value }))} rows={4} placeholder="How to work the new systems, what to watch in the first week, when paint fully cures… one point per line." data-testid="handover-firstweek-input" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Review link</Label><Input value={cfg.review_url || ""} onChange={(e) => setCfg((p) => ({ ...p, review_url: e.target.value }))} placeholder="https://g.page/… or Houzz/Google review URL" data-testid="handover-review-url-input" /></div>
            <div><Label>Review ask (optional)</Label><Input value={cfg.review_text || ""} onChange={(e) => setCfg((p) => ({ ...p, review_text: e.target.value }))} placeholder="Enjoyed working with us? A review means a lot." data-testid="handover-review-text-input" /></div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-5">
        <h3 className="section-title mb-1">What to include</h3>
        <p className="mb-3 text-sm text-muted-foreground">Turn sections on or off. They only appear when there's content.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {SECTIONS.map(([k, label]) => (
            <label key={k} className="flex items-center justify-between rounded-lg border px-3 py-2" data-testid={`handover-section-${k}`}>
              <span className="text-sm">{label}</span>
              <Switch checked={cfg.sections[k] !== false} onCheckedChange={(v) => setSection(k, v)} data-testid={`handover-toggle-${k}`} />
            </label>
          ))}
        </div>
        <Button className="mt-4 rounded-full" onClick={save} disabled={busy} data-testid="handover-save-btn">{busy ? "Saving…" : "Save changes"}</Button>
      </div>

      <div className="rounded-2xl border bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><Award className="h-5 w-5 text-accent" /><h3 className="section-title">Warranty register</h3></div>
          <Button size="sm" className="rounded-full" onClick={() => { setEditing(null); setWOpen(true); }} data-testid="warranty-add-btn"><Plus className="mr-1.5 h-4 w-4" />Add warranty</Button>
        </div>
        {warranties.loading && !warranties.data ? <Loading rows={2} /> : (warranties.data || []).length === 0 ? (
          <p className="rounded-lg border border-dashed p-5 text-center text-sm text-muted-foreground" data-testid="warranty-empty">No warranties yet. Add appliances, roofing, windows, flooring and anything else the homeowner should keep on record.</p>
        ) : (
          <div className="divide-y rounded-lg border" data-testid="warranty-list">
            {(warranties.data || []).map((w) => (
              <div key={w.id} className="flex flex-wrap items-start justify-between gap-3 p-3" data-testid="warranty-row">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{w.item}</p>
                  <p className="text-xs text-muted-foreground">{[w.provider, w.category, w.length].filter(Boolean).join(" · ")}{(w.files || []).length ? ` · ${w.files.length} file(s)` : ""}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(w); setWOpen(true); }} data-testid="warranty-edit-btn"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delWarranty(w.id)} data-testid="warranty-delete-btn"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <WarrantyDialog projectId={projectId} open={wOpen} onOpenChange={setWOpen} editing={editing} onSaved={() => { warranties.reload(); reload(); }} />
    </div>
  );
}
